import type { SearchClient } from '@quromedical/search';
import { set } from '@quromedical/utils';
import axios, { type AxiosInstance } from 'axios';
import {
  HealthbridgeLoginClient,
  type OAuth2Credentials,
} from '../Authentication';

import {
  BILLING_PRACTICE_NUMBER,
  PLATFORM_NAME,
  TREATING_PRACTICE_NUMBER,
} from '../constant';
import type {
  Administrator,
  AdministratorsResponse,
  FamilyCheckRequest,
  FamilyCheckResponse,
} from './';

type FamilyCheckClientOptions = {
  baseUrl: string;
  credentials: OAuth2Credentials;
  searchClient: SearchClient<Administrator>;
};

export class FamilyCheckClient {
  private apiVersion = '1.0';
  private familyCheckPath = `/family-check/api/${this.apiVersion}/FamilyCheck/`;
  private client: AxiosInstance;
  private readonly searchClient: SearchClient<Administrator>;

  constructor({
    baseUrl,
    credentials,
    searchClient,
  }: FamilyCheckClientOptions) {
    this.searchClient = searchClient;
    this.client = FamilyCheckClient.createAuthenticatedClient(
      baseUrl,
      credentials,
    );
  }

  private static createAuthenticatedClient = (
    baseURL: string,
    credentials: OAuth2Credentials,
  ): AxiosInstance => {
    const client = axios.create({
      baseURL,
    });

    client.interceptors.request.use(async (request) => {
      const loginClient = new HealthbridgeLoginClient(baseURL, credentials);
      const authToken = await loginClient.getToken();

      set(request, 'headers.Authorization', `Bearer ${authToken}`);

      return request;
    });

    return client;
  };

  public queryFamilyCheck = async (
    query: FamilyCheckRequest,
  ): Promise<FamilyCheckResponse> => {
    const params = {
      ...query,
      pmaName: PLATFORM_NAME,
      treatingPracticeNumber: TREATING_PRACTICE_NUMBER,
      billingPracticeNumber: BILLING_PRACTICE_NUMBER,
    };
    const path = `${this.familyCheckPath}Query`;

    const result = await this.client.post<FamilyCheckResponse>(
      path,
      undefined,
      {
        params,
      },
    );

    return result.data;
  };

  private getSchemes = async (): Promise<AdministratorsResponse> => {
    const result = await this.client.get<AdministratorsResponse>(
      `${this.familyCheckPath}Administrators`,
    );

    return result.data;
  };

  public renewSchemes = async () => {
    const schemes = await this.getSchemes();

    const cacheData = schemes
      .filter((d) => d.schemeCode)
      .map((scheme) => ({ ...scheme, objectID: scheme.schemeCode }));

    await this.searchClient.add(cacheData);
  };

  public searchSchemes = async (query: string, count: number) =>
    this.searchClient.search({ query, limit: count });
}
